import React, { useState } from 'react';
import { peep_client } from '~/src/api/peep';
import { useNavigate } from 'react-router-dom';
import { useAbort } from '~src/utils/useAbort';
import { Button, TextInput, Text, Paper, Stack, Title, Alert, SegmentedControl } from '@mantine/core';
import { AlertCircle } from 'lucide-react';

type SignupMode = 'passwordless' | 'password';

type SignupState =
  | { mode: 'passwordless'; status: 'idle'; email: string }
  | { mode: 'passwordless'; status: 'submitting'; email: string }
  | { mode: 'passwordless'; status: 'error'; email: string; error: string }
  | { mode: 'password'; status: 'idle'; email: string; password: string }
  | { mode: 'password'; status: 'submitting'; email: string; password: string }
  | { mode: 'password'; status: 'error'; email: string; password: string; error: string };

export default function Signup() {
  const [state, setState] = useState<SignupState>({ mode: 'passwordless', status: 'idle', email: '' });
  const navigate = useNavigate();
  const abortRef = useAbort();

  const switchMode = (newMode: SignupMode) => {
    if (newMode === 'passwordless') {
      setState({ mode: 'passwordless', status: 'idle', email: state.mode === 'password' ? state.email : '' });
    } else {
      setState({ mode: 'password', status: 'idle', email: state.email, password: '' });
    }
  };

  const updateEmail = (email: string) => {
    setState((s) => {
      if (s.status === 'submitting') return s;
      if (s.mode === 'passwordless') {
        return { mode: 'passwordless', status: 'idle', email };
      } else {
        return { mode: 'password', status: 'idle', email, password: s.password };
      }
    });
  };

  const updatePassword = (password: string) => {
    setState((s) => {
      if (s.status === 'submitting' || s.mode === 'passwordless') return s;
      return { mode: 'password', status: 'idle', email: s.email, password };
    });
  };

  const submit = async () => {
    if (state.status === 'submitting') return;

    const email = state.email;
    const password = state.mode === 'password' ? state.password : undefined;

    if (state.mode === 'passwordless') {
      setState({ mode: 'passwordless', status: 'submitting', email });
    } else {
      setState({ mode: 'password', status: 'submitting', email, password: state.password });
    }

    try {
      await peep_client.users.signup(email, password, { signal: abortRef.current.signal });
      navigate('/check-email', { state: { email } });
    } catch (e) {
      console.error(e);
      const errorMsg = typeof e === 'string' ? e : 'Something went wrong. Please try again.';

      if (state.mode === 'passwordless') {
        setState({ mode: 'passwordless', status: 'error', email, error: errorMsg });
      } else {
        setState({ mode: 'password', status: 'error', email, password: state.password, error: errorMsg });
      }
    }
  };

  const isLoading = state.status === 'submitting';
  const error = state.status === 'error' ? state.error : null;
  const canSubmit =
    state.mode === 'passwordless'
      ? state.email && (state.status === 'idle' || state.status === 'error')
      : state.email && state.password && (state.status === 'idle' || state.status === 'error');

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        minHeight: '100dvh',
        backgroundColor: 'var(--global-gray-lighter-7)',
        padding: '2rem',
      }}
    >
      <Paper
        style={{
          width: 'min(400px, 90vw)',
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '2rem',
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
          border: '1px solid #f0f0f0',
        }}
      >
        <Stack gap="lg">
          <div style={{ textAlign: 'center' }}>
            <Title order={2} style={{ fontFamily: 'Lato', color: '#333', marginBottom: '0.5rem' }}>
              Create Account
            </Title>
            <Text size="sm" style={{ fontFamily: 'Lato', color: '#666' }}>
              Join the conversation
            </Text>
          </div>

          {error && (
            <Alert icon={<AlertCircle size={16} />} color="red" style={{ fontFamily: 'Lato' }}>
              {error}
            </Alert>
          )}

          <SegmentedControl
            value={state.mode}
            onChange={(value) => switchMode(value as SignupMode)}
            disabled={isLoading}
            fullWidth
            data={[
              { label: 'Email only', value: 'passwordless' },
              { label: 'With password', value: 'password' },
            ]}
            styles={{
              root: { fontFamily: 'Lato' },
              label: { fontFamily: 'Lato' },
            }}
          />

          {state.mode === 'passwordless' && (
            <Text size="xs" c="dimmed" style={{ fontFamily: 'Lato', textAlign: 'center' }}>
              You'll receive a sign-in link via email each time you log in.
            </Text>
          )}

          <Stack gap="md">
            <TextInput
              placeholder="Enter your email"
              type="email"
              value={state.email}
              onChange={(e) => updateEmail(e.target.value)}
              disabled={isLoading}
              size="md"
              styles={{
                input: {
                  fontFamily: 'Lato',
                  borderRadius: '8px',
                  border: '1px solid #e0e0e0',
                },
              }}
            />
            {state.mode === 'password' && (
              <TextInput
                placeholder="Enter your password"
                type="password"
                value={state.password}
                onChange={(e) => updatePassword(e.target.value)}
                disabled={isLoading}
                size="md"
                styles={{
                  input: {
                    fontFamily: 'Lato',
                    borderRadius: '8px',
                    border: '1px solid #e0e0e0',
                  },
                }}
              />
            )}
          </Stack>

          <Stack gap="sm">
            <Button
              onClick={submit}
              disabled={!canSubmit}
              loading={isLoading}
              size="md"
              style={{
                backgroundColor: 'var(--global-main)',
                fontFamily: 'Lato',
                fontWeight: '600',
                borderRadius: '8px',
              }}
              fullWidth
            >
              Create Account
            </Button>

            <Button
              onClick={() => navigate('/signin')}
              variant="subtle"
              size="md"
              style={{
                fontFamily: 'Lato',
                color: 'var(--global-main)',
              }}
              fullWidth
            >
              Already have an account? Sign in
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </div>
  );
}
